import React, { useRef } from "react"
import { cn } from "../utils"
import { useRender } from "@base-ui/react/use-render"
import { useTrackScrollOverflow } from "../use-track-scroll-overflow"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderOverflowScrollProps = {
  /**
   * Scrolled content. Give the immediate child an intrinsic width (`w-max`) so
   * it overflows the container instead of shrinking to fit it.
   */
  children?: React.ReactNode
  /** Applied to the positioning wrapper, not the scroll container. */
  className?: string
  /**
   * Element to render as the scroll container, so the caller keeps ownership of
   * the semantic element (`<nav>`, `Tabs.List`). Defaults to a `<div>`.
   */
  render?: useRender.RenderProp
}

// =============================================================================
// Styles
// =============================================================================

const wrapperStyles = ["relative min-w-0"]

const scrollStyles = [
  // Padding keeps focus rings from being clipped by the scroll container.
  "p-1",
  "overflow-x-auto overscroll-x-contain",
  // Overlay scrollbars stay out of the way on macOS, but a persistent
  // scrollbar elsewhere would eat into the header row's height.
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
]

const fadeStyles = [
  "pointer-events-none absolute inset-y-0 w-8",
  "transition-opacity duration-200"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Horizontally scrollable container for the secondary page header's center
 * content, with a fade at whichever edge still has content beyond it.
 *
 * Internal helper composed by `PageSecondaryHeader.Tabs`,
 * `PageSecondaryHeader.TabLinks`, and `PageSecondaryHeader.Steps` — not part of
 * the public `PageSecondaryHeader.*` API.
 */
export function PageSecondaryHeaderOverflowScroll({
  children,
  render,
  className
}: PageSecondaryHeaderOverflowScrollProps) {
  const ref = useRef<HTMLElement>(null)
  const { hasLeftOverflow, hasRightOverflow } = useTrackScrollOverflow(ref)

  const scrollElement = useRender({
    render,
    ref,
    props: { className: cn(scrollStyles), children }
  })

  return (
    <div className={cn(wrapperStyles, className)}>
      {scrollElement}
      <div
        className={cn(
          fadeStyles,
          "left-0 bg-gradient-to-r from-white to-white/0",
          hasLeftOverflow ? "opacity-100" : "opacity-0"
        )}
        aria-hidden="true"
      />
      <div
        className={cn(
          fadeStyles,
          "right-0 bg-gradient-to-l from-white to-white/0",
          hasRightOverflow ? "opacity-100" : "opacity-0"
        )}
        aria-hidden="true"
      />
    </div>
  )
}

PageSecondaryHeaderOverflowScroll.displayName =
  "PageSecondaryHeaderOverflowScroll"
